/**
 * Year6FunnelDashboard — /admin/year6/funnel
 *
 * Operator control panel for the Year 6 Memory Book funnel.
 * Reads GET /api/admin/year6/funnel and renders:
 *   1. Verdict banner (decision engine output)
 *   2. 3 big metrics (parent view / conversion / child selection)
 *   3. Vertical funnel (7 stages with ↓ connectors)
 *   4. Share-channel breakdown (gradient bars)
 *   5. Guidance block (recommendations + priority action)
 *   6. Last-updated footer
 *
 * "Dashboard → decision engine."  Admin-gated by backend (403 if not is_admin).
 */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Warning, ArrowDown, ArrowsClockwise, GraduationCap,
} from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STAGE_DEFS = [
  { key: 'emails_sent', label: 'Emails sent' },
  { key: 'email_clicks', label: 'Email clicks' },
  { key: 'parent_views', label: 'Parent views' },
  { key: 'child_selections', label: 'Child selected' },
  { key: 'checkouts_started', label: 'Checkout started' },
  { key: 'purchases', label: 'Purchases (£99)' },
  { key: 'downloads', label: 'Downloads' },
];

const VERDICT_TONE = {
  healthy:                   { tone: 'green',  emoji: '🟢', label: 'Healthy' },
  distribution_broken:       { tone: 'red',    emoji: '🔴', label: 'Distribution broken' },
  conversion_broken:         { tone: 'red',    emoji: '🔴', label: 'Conversion broken' },
  personalisation_underused: { tone: 'yellow', emoji: '🟡', label: 'Personalisation underused' },
  mixed:                     { tone: 'orange', emoji: '🟠', label: 'Mixed signals' },
  no_data_yet:               { tone: 'slate',  emoji: '⚪️', label: 'No data yet' },
};

const TONE_CLASSES = {
  green:  { bg: 'bg-emerald-500/10',  border: 'border-emerald-500/25', text: 'text-emerald-300' },
  red:    { bg: 'bg-rose-500/10',     border: 'border-rose-500/25',    text: 'text-rose-300' },
  yellow: { bg: 'bg-amber-400/10',    border: 'border-amber-400/30',   text: 'text-amber-300' },
  orange: { bg: 'bg-orange-500/10',   border: 'border-orange-500/30',  text: 'text-orange-300' },
  slate:  { bg: 'bg-white/[0.04]',    border: 'border-white/15',       text: 'text-white/70' },
};

const SHARE_CHANNEL_LABEL = {
  whatsapp: 'WhatsApp',
  copy: 'Copy link',
  email: 'Email',
  unknown: 'Other',
};

function metricTone(pct) {
  if (pct === null || pct === undefined) return 'text-white/70';
  if (pct >= 40) return 'text-emerald-300';
  if (pct >= 20) return 'text-amber-300';
  return 'text-rose-300';
}

function relativeTime(iso) {
  if (!iso) return '';
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return '';
  const s = Math.max(0, Math.floor((Date.now() - t) / 1000));
  if (s < 5) return 'just now';
  if (s < 60) return `${s} seconds ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m} minute${m === 1 ? '' : 's'} ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} hour${h === 1 ? '' : 's'} ago`;
  const d = Math.floor(h / 24);
  return `${d} day${d === 1 ? '' : 's'} ago`;
}

export default function Year6FunnelDashboard() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async ({ silent } = { silent: false }) => {
    if (silent) setRefreshing(true); else setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API}/admin/year6/funnel`);
      setData(res.data);
    } catch (e) {
      if (e.response?.status === 403) setError('Admin access required');
      else if (e.response?.status === 401) {
        toast.error('Please log in');
        navigate('/login');
      } else setError('Could not load Year 6 funnel');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [navigate]);

  useEffect(() => { load(); }, [load]);

  if (error) {
    return (
      <div className="min-h-screen bg-[#0F1014] flex items-center justify-center text-white" data-testid="year6-funnel-error">
        <div className="text-center px-6 max-w-sm">
          <Warning size={28} weight="fill" className="text-amber-300 mx-auto mb-3" />
          <p className="font-black mb-1">{error}</p>
          <button
            onClick={() => navigate('/admin/growth')}
            className="mt-4 px-4 py-2 rounded-lg text-xs font-bold bg-white/10 border border-white/15"
            data-testid="year6-funnel-error-back-btn"
          >
            Back to Growth
          </button>
        </div>
      </div>
    );
  }

  if (loading || !data) {
    return (
      <div className="min-h-screen bg-[#0F1014] flex items-center justify-center" data-testid="year6-funnel-loading">
        <div className="animate-spin rounded-full h-10 w-10 border-4 border-[#B89762]/40 border-t-transparent" />
      </div>
    );
  }

  const stages = data.stages || {};
  const ratios = data.ratios || {};
  const verdictKey = data.verdict || 'no_data_yet';
  const verdictMeta = VERDICT_TONE[verdictKey] || VERDICT_TONE.mixed;
  const verdictTone = TONE_CLASSES[verdictMeta.tone];
  const totalShares = Object.values(data.share_channels || {}).reduce((a, b) => a + b, 0);

  return (
    <div className="min-h-screen bg-[#0F1014] text-white pb-12" data-testid="year6-funnel-dashboard">
      {/* Header */}
      <div className="border-b border-white/8 bg-[#0F1014]/95 backdrop-blur sticky top-0 z-20">
        <div className="max-w-5xl mx-auto px-5 py-4 flex items-center justify-between gap-4">
          <button
            onClick={() => navigate('/admin/growth')}
            className="text-white/60 hover:text-white text-xs font-bold flex items-center gap-1.5"
            data-testid="year6-funnel-back-btn"
          >
            <ArrowLeft size={14} weight="bold" /> Growth
          </button>
          <div className="flex-1 min-w-0">
            <h1
              className="text-lg font-black truncate flex items-center gap-2"
              style={{ fontFamily: '"Playfair Display", Georgia, serif' }}
              data-testid="year6-funnel-title"
            >
              <GraduationCap size={20} weight="fill" className="text-[#C29D52]" />
              Year 6 funnel
            </h1>
            <p className="text-[10px] uppercase tracking-[0.18em] text-white/40 mt-0.5">
              Schools → Parents → Purchases
            </p>
          </div>
          <button
            onClick={() => load({ silent: true })}
            disabled={refreshing}
            className="text-white/60 hover:text-white disabled:opacity-40 px-2.5 py-1.5 rounded-lg bg-white/[0.05] border border-white/10 flex items-center gap-1.5 text-[11px] font-bold"
            data-testid="year6-funnel-refresh-btn"
          >
            <ArrowsClockwise size={12} weight="bold" className={refreshing ? 'animate-spin' : ''} />
            {refreshing ? 'Refreshing' : 'Refresh'}
          </button>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-5 py-7 space-y-6">

        {/* 1. VERDICT BANNER */}
        <div
          className={`rounded-2xl px-5 py-4 border ${verdictTone.bg} ${verdictTone.border}`}
          data-testid="year6-funnel-verdict"
          data-verdict={verdictKey}
        >
          <div className="flex items-baseline gap-2">
            <span className="text-lg leading-none">{verdictMeta.emoji}</span>
            <p className={`font-black text-base ${verdictTone.text}`} data-testid="year6-funnel-verdict-headline">
              {data.headline || verdictMeta.label}
            </p>
          </div>
          {data.subline && (
            <p className="text-sm text-white/70 mt-1" data-testid="year6-funnel-verdict-subline">
              {data.subline}
            </p>
          )}
        </div>

        {/* 2. KEY METRICS */}
        <section data-testid="year6-funnel-metrics">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <MetricCard
              label="Parent View Rate"
              pct={ratios.parent_view_rate_pct}
              hint="Emails → views"
              testId="metric-parent-view-rate"
            />
            <MetricCard
              label="Conversion Rate"
              pct={ratios.conversion_rate_pct}
              hint="Views → £99 purchases"
              testId="metric-conversion-rate"
            />
            <MetricCard
              label="Child Selection Rate"
              pct={ratios.child_selection_rate_pct}
              hint="Views → child selected"
              testId="metric-child-selection-rate"
            />
          </div>
          <div className="grid grid-cols-2 gap-3 mt-3">
            <SecondaryMetric
              label="Email click-through"
              pct={ratios.email_click_through_rate_pct}
              testId="metric-email-ctr"
            />
            <SecondaryMetric
              label="Checkout completion"
              pct={ratios.checkout_completion_rate_pct}
              testId="metric-checkout-completion"
            />
          </div>
        </section>

        {/* 3. FUNNEL STEPS */}
        <section
          className="rounded-2xl border border-white/10 bg-[#15161B] p-5"
          data-testid="year6-funnel-steps"
        >
          <div className="flex items-baseline justify-between mb-3">
            <h2 className="text-sm font-black">Funnel</h2>
            <p className="text-[10px] uppercase tracking-[0.16em] text-white/35">
              {stages.total_events || 0} year 6 event{(stages.total_events || 0) === 1 ? '' : 's'}
            </p>
          </div>
          <div>
            {STAGE_DEFS.map((s, i) => (
              <React.Fragment key={s.key}>
                <FunnelStepRow
                  stageKey={s.key}
                  label={s.label}
                  value={stages[s.key] ?? 0}
                />
                {i < STAGE_DEFS.length - 1 && (
                  <div
                    className="flex justify-center py-1 text-white/20"
                    aria-hidden="true"
                  >
                    <ArrowDown size={12} weight="bold" />
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>
        </section>

        {/* 4. SHARE BREAKDOWN */}
        {totalShares > 0 ? (
          <section
            className="rounded-2xl border border-white/10 bg-white/[0.02] p-5"
            data-testid="year6-funnel-share-breakdown"
          >
            <div className="flex items-baseline justify-between mb-3">
              <h2 className="text-sm font-black">Share channels</h2>
              <p className="text-[10px] uppercase tracking-[0.16em] text-white/35">
                {totalShares} share{totalShares === 1 ? '' : 's'} total
              </p>
            </div>
            <div>
              {Object.entries(data.share_channels || {})
                .sort((a, b) => b[1] - a[1])
                .map(([ch, n]) => (
                  <ShareBar
                    key={ch}
                    label={SHARE_CHANNEL_LABEL[ch] || ch}
                    value={n}
                    total={totalShares}
                    testId={`share-bar-${ch}`}
                  />
                ))}
            </div>
          </section>
        ) : (
          <section
            className="rounded-2xl border border-white/10 bg-white/[0.02] p-5 text-center"
            data-testid="year6-funnel-share-empty"
          >
            <p className="text-xs text-white/40 uppercase tracking-[0.16em] mb-1">Share channels</p>
            <p className="text-sm text-white/60">No shares recorded yet.</p>
          </section>
        )}

        {/* 5. RECOMMENDATIONS */}
        {data.recommendations && data.recommendations.length > 0 && (
          <section
            className="rounded-2xl bg-[#1B1C22] border border-white/5 p-5"
            data-testid="year6-funnel-guidance"
          >
            <p className="text-[10px] uppercase tracking-[0.18em] text-white/50 mb-3">
              What to do next
            </p>
            {data.priority_action && (
              <div
                className="rounded-xl bg-[#B89762]/30/[0.06] border border-[#B89762]/40/20 px-4 py-3 mb-3"
                data-testid="year6-funnel-priority-action"
              >
                <p className="text-[10px] uppercase tracking-[0.18em] text-[#C29D52]/70 mb-0.5">
                  Focus on this first
                </p>
                <p className="text-sm font-black text-[#F4E9D2]">
                  👉 {data.priority_action}
                </p>
              </div>
            )}
            <ul className="space-y-2 text-sm text-white/80">
              {data.recommendations.map((r, i) => (
                <li
                  key={i}
                  className="flex gap-2"
                  data-testid={`year6-funnel-rec-${i}`}
                >
                  <span className="text-white/40 shrink-0">→</span>
                  <span>{r}</span>
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* 6. FOOTER */}
        <div className="text-[11px] text-white/40 text-right" data-testid="year6-funnel-last-updated">
          Last updated {relativeTime(data.generated_at) || 'just now'}
        </div>
      </div>
    </div>
  );
}

function MetricCard({ label, pct, hint, testId }) {
  const display = (pct === null || pct === undefined) ? '—' : `${pct}%`;
  const tone = metricTone(pct);
  return (
    <div
      className="bg-[#1B1C22] p-5 rounded-2xl border border-white/5"
      data-testid={testId}
    >
      <div className={`text-3xl font-black ${tone}`} data-testid={`${testId}-value`}>
        {display}
      </div>
      <div className="text-sm text-white/70 mt-1 font-bold">{label}</div>
      {hint && <div className="text-[11px] text-white/40 mt-0.5">{hint}</div>}
    </div>
  );
}

function SecondaryMetric({ label, pct, testId }) {
  const display = (pct === null || pct === undefined) ? '—' : `${pct}%`;
  return (
    <div
      className="rounded-xl border border-white/8 bg-white/[0.02] px-4 py-3"
      data-testid={testId}
    >
      <p className="text-[10px] uppercase tracking-[0.16em] text-white/45">{label}</p>
      <p className="text-lg font-black text-white mt-0.5" data-testid={`${testId}-value`}>
        {display}
      </p>
    </div>
  );
}

function FunnelStepRow({ stageKey, label, value }) {
  return (
    <div
      className="flex items-center justify-between py-2"
      data-testid={`year6-funnel-stage-${stageKey}`}
    >
      <span className="text-white/70 text-sm">{label}</span>
      <span
        className="font-black text-base text-white"
        data-testid={`year6-funnel-stage-${stageKey}-value`}
      >
        {Number(value || 0).toLocaleString()}
      </span>
    </div>
  );
}

function ShareBar({ label, value, total, testId }) {
  const pct = total ? Math.round((value / total) * 100) : 0;
  return (
    <div className="mb-3 last:mb-0" data-testid={testId}>
      <div className="flex justify-between text-sm text-white/75">
        <span>{label}</span>
        <span className="font-bold" data-testid={`${testId}-pct`}>{pct}%</span>
      </div>
      <div className="w-full bg-white/10 h-2 rounded mt-1 overflow-hidden">
        <div
          className="h-2 rounded"
          style={{
            width: `${pct}%`,
            background: 'linear-gradient(90deg, #C29D52, #B89762)',
          }}
        />
      </div>
    </div>
  );
}
